import { z } from 'zod';
import { montarCaminho, rotas, } from './rotas.js';
/**
 * Cliente HTTP gerado do contrato.
 *
 * Substitui os tres clientes Supabase que hoje vivem no navegador com a chave do
 * banco dentro. O navegador passa a falar SO com a API — e por isso a autorizacao
 * pode viver em codigo testavel em vez de policy espalhada em cinco arquivos .sql.
 */
export class ErroApiNyroh extends Error {
    status;
    codigo;
    detalhes;
    rota;
    constructor(status, codigo, mensagem, detalhes, rota) {
        super(mensagem);
        this.status = status;
        this.codigo = codigo;
        this.detalhes = detalhes;
        this.rota = rota;
        this.name = 'ErroApiNyroh';
    }
    /** 401/403 tem tratamento diferente na tela: um pede login, o outro nao adianta repetir. */
    get precisaAutenticar() {
        return this.status === 401;
    }
    get proibido() {
        return this.status === 403;
    }
    get naoEncontrado() {
        return this.status === 404;
    }
}
/**
 * ============================================================================
 * ERRO DE VALIDACAO LEGIVEL
 * ============================================================================
 *
 * O comentario da validacao sempre prometeu "erro aqui, COM O NOME DO CAMPO".
 * Ele nao entregava: `schema.parse()` lanca um `ZodError` cru, cuja `.message` e
 * o JSON das issues. A tela mostrava isso ao operador:
 *
 *   [ { "origin": "string", "code": "too_big", "maximum": 120, "inclusive": true,
 *       "path": [ "clientes", 93, "slug" ], "message": "Too big: ..." } ]
 *
 * Verdadeiro e inutil. Nem diz QUAL projeto e o 93, nem o que fazer.
 *
 * Aqui o caminho vira texto — `clientes[93].slug` — e o erro passa a ser um
 * `ErroApiNyroh` com status 422, o mesmo que o servidor devolveria. Quem chama
 * ja sabe tratar essa classe, e as `issues` continuam em `detalhes` para quem
 * quiser traduzir o indice em nome de projeto.
 */
function caminhoLegivel(path) {
    return path.reduce((acc, p) => {
        if (typeof p === 'number')
            return `${acc}[${p}]`;
        return acc ? `${acc}.${String(p)}` : String(p);
    }, '');
}
function validarEntrada(schema, valor, onde, rota) {
    const r = schema.safeParse(valor);
    if (r.success)
        return r.data;
    const problemas = r.error.issues
        .map((i) => `${caminhoLegivel(i.path) || '(raiz)'}: ${i.message}`)
        .slice(0, 5);
    const resto = r.error.issues.length - problemas.length;
    throw new ErroApiNyroh(422, 'entrada_invalida', `${rota}: ${onde} invalido — ${problemas.join('; ')}` +
        (resto > 0 ? ` (e mais ${resto})` : ''), r.error.issues, rota);
}
const semCorpo = new Set(['GET', 'DELETE']);
export function criarClienteApi(opcoes) {
    const base = opcoes.baseUrl.replace(/\/+$/, '');
    const fetchFn = opcoes.fetchImpl ?? globalThis.fetch;
    const timeoutMs = opcoes.timeoutMs ?? 30_000;
    if (typeof fetchFn !== 'function') {
        throw new Error('criarClienteApi: nenhum fetch disponivel neste runtime.');
    }
    async function chamar(nome, entrada = {}, opcoesDaChamada = {}) {
        const tetoDaChamada = opcoesDaChamada.timeoutMs ?? timeoutMs;
        const def = rotas[nome];
        const e = entrada;
        // Valida ANTES de sair da maquina. Corpo errado vira erro aqui, com o nome do
        // campo, em vez de um 400 generico depois de atravessar a rede.
        let corpo = e.corpo;
        if ('corpo' in def && def.corpo) {
            corpo = validarEntrada(def.corpo, e.corpo, 'corpo', nome);
        }
        let query = e.query;
        if ('query' in def && def.query && e.query !== undefined) {
            query = validarEntrada(def.query, e.query, 'query', nome);
        }
        const caminho = montarCaminho(def.caminho, e.params);
        const url = new URL(base + caminho);
        if (query) {
            for (const [chave, valor] of Object.entries(query)) {
                if (valor === undefined || valor === null || valor === '')
                    continue;
                if (Array.isArray(valor)) {
                    for (const v of valor)
                        url.searchParams.append(chave, String(v));
                }
                else {
                    url.searchParams.set(chave, String(valor));
                }
            }
        }
        const cabecalhos = { accept: 'application/json' };
        const token = await opcoes.obterToken?.();
        if (token)
            cabecalhos.authorization = `Bearer ${token}`;
        if (corpo !== undefined && !semCorpo.has(def.metodo)) {
            cabecalhos['content-type'] = 'application/json';
        }
        const controle = tetoDaChamada > 0 ? new AbortController() : null;
        const alarme = controle ? setTimeout(() => controle.abort(), tetoDaChamada) : null;
        let resposta;
        try {
            resposta = await fetchFn(url.toString(), {
                method: def.metodo,
                headers: cabecalhos,
                body: corpo !== undefined && !semCorpo.has(def.metodo) ? JSON.stringify(corpo) : undefined,
                credentials: opcoes.comCredenciais === false ? 'omit' : 'include',
                signal: controle?.signal,
            });
        }
        catch (causa) {
            const erro = new ErroApiNyroh(0, 'rede', causa instanceof Error && causa.name === 'AbortError'
                ? `A API nao respondeu em ${tetoDaChamada}ms (${def.metodo} ${caminho}).`
                : `Nao consegui falar com a API em ${base}. Ela esta no ar?`, causa, nome);
            opcoes.aoErro?.(erro);
            throw erro;
        }
        finally {
            if (alarme)
                clearTimeout(alarme);
        }
        const texto = await resposta.text();
        const json = texto ? seguroJson(texto) : {};
        if (!resposta.ok) {
            const analisado = z
                .object({ erro: z.object({ codigo: z.string(), mensagem: z.string() }).loose() })
                .safeParse(json);
            const erro = new ErroApiNyroh(resposta.status, analisado.success ? analisado.data.erro.codigo : `http_${resposta.status}`, analisado.success
                ? analisado.data.erro.mensagem
                : `${def.metodo} ${caminho} respondeu ${resposta.status}.`, json, nome);
            opcoes.aoErro?.(erro);
            throw erro;
        }
        // A resposta tambem e validada. Se a API mudar o formato sem mudar o contrato,
        // quebra aqui, com o campo divergente no erro — e nao tres telas adiante.
        const validada = def.resposta.safeParse(json);
        if (!validada.success) {
            const erro = new ErroApiNyroh(resposta.status, 'resposta_fora_do_contrato', `A resposta de ${nome} nao bate com o contrato: ${validada.error.issues
                .slice(0, 3)
                .map((i) => `${i.path.join('.') || '(raiz)'}: ${i.message}`)
                .join('; ')}`, validada.error.issues, nome);
            opcoes.aoErro?.(erro);
            throw erro;
        }
        return validada.data;
    }
    return {
        chamar,
        /** Base da API, para montar URL de recurso que nao passa por `chamar` (SSE, download). */
        baseUrl: base,
    };
}
function seguroJson(texto) {
    try {
        return JSON.parse(texto);
    }
    catch {
        return { erro: { codigo: 'resposta_nao_json', mensagem: texto.slice(0, 400) } };
    }
}
/**
 * Deriva a URL publica de um objeto do R2.
 *
 * Existe aqui, e nao em cada app, porque a base do CDN muda entre local (MinIO),
 * homolog e producao. Guardar a CHAVE no banco e derivar a URL na leitura e o que
 * permite trocar o dominio do CDN sem reescrever 100 mil linhas.
 */
export function urlDoObjeto(baseCdn, chaveR2) {
    return `${baseCdn.replace(/\/+$/, '')}/${chaveR2.replace(/^\/+/, '')}`;
}
//# sourceMappingURL=cliente-http.js.map